import { useEffect, useState, useMemo, useContext } from "react";
import { View, Text, StyleSheet, Pressable } from "react-native";

import { appColors, cardShadow } from "../constants/colors";
import { calculateTableValue } from "../utils/helpers";
import { CalculationsContext } from "../store/calculationsCtx";

const GPTTable = ({ currency }) => {
  const [start, setStart] = useState(0);
  const [rows, setRows] = useState(5);
  const [page, setPage] = useState(1);
  const [upperLimitReached, setUpperLimitReached] = useState(false);
  const [lowerLimitReached, setLowerLimitReached] = useState(true);

  const calculationsCtx = useContext(CalculationsContext);
  const resultsTable = calculationsCtx.tableResults;

  const currencyObj = { style: "currency", currency: currency?.code || "USD" };
  const locale = currency?.locale || "en-US";

  const update = useMemo(() => resultsTable, [resultsTable]);

  useEffect(() => {
    setPage(1);
    setRows(5);
    setStart(0);
    setLowerLimitReached(true);
    setUpperLimitReached(false);
  }, [update]);

  const extras = calculationsCtx?.inputs?.extraPayments?.map(item => ({
    ...item,
    amount: item.amount === "" ? "0" : item.amount,
  }));

  const rates = calculationsCtx?.inputs?.rateChanges?.map(item => ({
    ...item,
    rate: item.rate === "" ? "0" : item.rate,
  }));

  const data = calculateTableValue({
    ...resultsTable,
    extraPayments: extras,
    rateChanges: rates,
    serviceFee: calculationsCtx?.inputs?.serviceFee,
    balloon: calculationsCtx?.inputs?.balloon,
  });

  const totalInterest = data.reduce((sum, m) => sum + parseFloat(m.interest), 0);
  const totalPaid = data.reduce((sum, m) => {
    const balloon = m.isBalloon ? m.remainingBalance : 0;
    return sum + parseFloat(m.instalment) + balloon;
  }, 0);
  const loan = data.reduce((sum, m) => sum + parseFloat(m.principal), 0);
  const totalFees = data.reduce((sum, m) => sum + (parseFloat(m.serviceFee) || 0), 0);
  const balloonObj = data.filter(d => d.isBalloon);
  const balloon = balloonObj[0]?.remainingBalance || 0;

  useEffect(() => {
    calculationsCtx.setResults2({
      total: totalPaid.toLocaleString(locale, { style: "currency", currency: currencyObj.currency }),
      total2: totalPaid,
      fees: totalFees.toLocaleString(locale, { style: "currency", currency: currencyObj.currency }),
      feesNumber: totalFees,
      balloon,
      balloonValue: balloon.toLocaleString(locale, { style: "currency", currency: currencyObj.currency }),
      interest: totalInterest.toLocaleString(locale, { style: "currency", currency: currencyObj.currency }),
      interest2: totalInterest,
      loan: loan.toLocaleString(locale, { style: "currency", currency: currencyObj.currency }),
      loan2: loan,
      schedule: data,
    });
  }, [totalInterest, totalPaid, totalFees]);

  if (data.length === 0) return null;

  const lastPage = Math.ceil(data.length / 5);

  function paginationHandler(dir) {
    if (dir === "forward") {
      if (lowerLimitReached) setLowerLimitReached(false);
      setStart(s => s + 5);
      setRows(r => r + 5);
      setPage(p => {
        if (p + 1 === lastPage) setUpperLimitReached(true);
        return p + 1;
      });
    }
    if (dir === "back") {
      if (upperLimitReached) setUpperLimitReached(false);
      setStart(s => s - 5);
      setRows(r => r - 5);
      setPage(p => {
        if (p - 1 === 1) setLowerLimitReached(true);
        return p - 1;
      });
    }
  }

  const fmt = val => val.toLocaleString(locale, { style: "currency", currency: currencyObj.currency });

  return (
    <View style={styles.card}>
      {/* Card header */}
      <View style={styles.cardHeader}>
        <Text style={styles.cardTitle}>Amortisation schedule</Text>
        <View style={styles.pager}>
          <Pressable
            disabled={lowerLimitReached}
            onPress={() => paginationHandler("back")}
            style={({ pressed }) => [styles.pgBtn, lowerLimitReached && styles.pgBtnDisabled, pressed && styles.pgBtnPressed]}
          >
            <Text style={[styles.pgBtnText, lowerLimitReached && styles.pgBtnTextDisabled]}>‹</Text>
          </Pressable>
          <Text style={styles.pageLabel}>{page} / {lastPage}</Text>
          <Pressable
            disabled={upperLimitReached}
            onPress={() => paginationHandler("forward")}
            style={({ pressed }) => [styles.pgBtn, upperLimitReached && styles.pgBtnDisabled, pressed && styles.pgBtnPressed]}
          >
            <Text style={[styles.pgBtnText, upperLimitReached && styles.pgBtnTextDisabled]}>›</Text>
          </Pressable>
        </View>
      </View>

      {/* Table header */}
      <View style={styles.tableHeader}>
        <Text style={[styles.headerCell, styles.monthCol]}>Month</Text>
        <Text style={[styles.headerCell, styles.numCol]}>Instalment</Text>
        <Text style={[styles.headerCell, styles.numCol]}>Interest</Text>
        <Text style={[styles.headerCell, styles.numCol]}>Principal</Text>
        <Text style={[styles.headerCell, styles.numCol]}>Balance</Text>
      </View>

      {/* Rows */}
      {data.slice(start, rows).map((row, index) => (
        <View key={index} style={[styles.tableRow, index % 2 === 0 && styles.tableRowAlt]}>
          <Text style={[styles.cell, styles.monthCol, styles.monthText]}>{row.term}</Text>
          <Text style={[styles.cell, styles.numCol]}>{fmt(row.instalment)}</Text>
          <Text style={[styles.cell, styles.numCol]}>{fmt(row.interest)}</Text>
          <Text style={[styles.cell, styles.numCol]}>{fmt(row.principal)}</Text>
          <Text style={[styles.cell, styles.numCol]}>{fmt(row.remainingBalance)}</Text>
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    width: "90%",
    backgroundColor: appColors.cardBg,
    borderRadius: 16,
    borderWidth: 0.5,
    borderColor: appColors.border,
    overflow: "hidden",
    ...cardShadow(),
  },

  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: appColors.borderLight,
  },

  cardTitle: {
    fontSize: 12,
    fontWeight: "500",
    color: appColors.textPrimary,
  },

  pager: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },

  pgBtn: {
    width: 26,
    height: 26,
    borderRadius: 8,
    backgroundColor: appColors.primaryTint,
    alignItems: "center",
    justifyContent: "center",
  },

  pgBtnDisabled: {
    opacity: 0.35,
  },

  pgBtnPressed: {
    backgroundColor: appColors.primaryTintDark,
  },

  pgBtnText: {
    fontSize: 16,
    color: appColors.primary,
    lineHeight: 20,
  },

  pgBtnTextDisabled: {
    color: appColors.textMuted,
  },

  pageLabel: {
    fontSize: 11,
    color: appColors.textSecondary,
    minWidth: 36,
    textAlign: "center",
  },

  tableHeader: {
    flexDirection: "row",
    paddingHorizontal: 10,
    paddingVertical: 7,
    backgroundColor: appColors.statBg,
    borderBottomWidth: 0.5,
    borderBottomColor: appColors.border,
  },

  tableRow: {
    flexDirection: "row",
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: appColors.borderLight,
  },

  tableRowAlt: {
    backgroundColor: appColors.rowAlt,
  },

  headerCell: {
    fontSize: 10,
    fontWeight: "500",
    color: appColors.textSecondary,
  },

  cell: {
    fontSize: 10,
    color: appColors.textPrimary,
  },

  monthCol: {
    width: 36,
  },

  numCol: {
    flex: 1,
    textAlign: "right",
  },

  monthText: {
    color: appColors.textSecondary,
  },
});

export default GPTTable;
